// The tutorial registry's shapes. Plain data, no React, no imports from the app — scripts/check-tutorials.mjs loads these
// files under node to verify every anchor, budget and section before a deploy, so nothing here may need a bundler.
//
// A tutorial is a tour (ui/tour.tsx): ordered coachmark steps, each pointing at a real control by its `data-tour` anchor.
// Three tiers (TUTORIALS-PLAN.md):
//   1  First five minutes — one per role, auto-starts once for a first-timer, ≤ 8 steps
//   2  Areas — what a job's tools are and why they exist, ≤ 10 steps, the TOC of its tier-3 children
//   3  Tools — one tool, one real outcome, ≤ 6 steps (or `budget: "long"` with a reason)

export type Tier = 1 | 2 | 3;
/** The five areas of TUTORIALS-PLAN.md §9. */
export type Area = "prep" | "table" | "players" | "data" | "playing";
export type Role = "gm" | "player";

/** The pages a step can live on. A signed-in player owns `play`/`sheet`; `table` is the shared board; the GM owns the rest. */
export type Surface = "welcome" | "new-campaign" | "workspace" | "play" | "sheet" | "table";

export type TourStep = {
  /** Stable id, saved to remember where a tour paused. Never reuse one across tutorials. */
  key: string;
  surface: Surface;
  title: string;
  /** Plain text; a blank line starts a new paragraph. ≤ 60 words — the gate counts. */
  body: string;
  /** The `data-tour` value of the element to spotlight. Omit for a centred card with no spotlight. */
  anchor?: string;
  /** Where the bubble sits relative to the anchor. Auto-flips to stay on screen. */
  placement?: "top" | "bottom" | "left" | "right";
  /** Clicking the anchor advances the tour (and still does whatever the control does). For "teach by doing" steps. */
  advanceOnClick?: boolean;
  /** The address that reaches this step's surface, when the previous step is elsewhere. Omit when the surface is only ever RESUMED into (the table). */
  goto?: string;
  /** If the anchor never appears, skip this step instead of showing the bubble centred. */
  skipIfMissing?: boolean;
  /** A ≤10 s looping clip (a path under /tutorials/), only where a gesture cannot be told in words. */
  clip?: string;
  /**
   * The rail card (and tab) this step's control lives on. The engine opens it when the step shows, so a tool tutorial never
   * asks the user to click a TOGGLE: found live 2026-10-02 — "Open F.o.W." closed the card when it was already open, and
   * every step after it pointed at nothing. Section and tab ids are right-rail.tsx's; the gate checks them.
   */
  open?: { section: string; tab?: string };
};

export type Tutorial = {
  /** Stable id — also the localStorage key suffix and the `?` button's target. */
  id: string;
  tier: Tier;
  /** Tier 2 and 3 belong to an area; tier 1 does not. */
  area?: Area;
  role: Role;
  /** Tier 3: the tier-2 tutorial it hangs under (its id). */
  parent?: string;
  title: string;
  /** One line for the Tutorials page. */
  blurb: string;
  /** Minutes, honestly — shown on the Tutorials page so the choice is the user's. */
  minutes: number;
  /** TOOL-INVENTORY.md section numbers this tutorial teaches. The gate requires every section 1–26 to appear somewhere. */
  covers: number[];
  /** Over the tier's step budget, on purpose. Needs `budgetWhy`. */
  budget?: "long";
  budgetWhy?: string;
  /** Tier 1 only: the surface a first-timer is on when this should begin by itself. */
  autoStart?: Surface;
  /** Tier 1 only: begins only on a phone (or only off one). Absent = either. */
  phone?: boolean;
  steps: TourStep[];
};

/** The step budgets by tier. A tutorial may declare `budget: "long"` with a reason; the TOC shows its minutes either way. */
export const STEP_BUDGET: Record<Tier, number> = { 1: 8, 2: 10, 3: 6 };
export const BODY_WORD_BUDGET = 60;
export const AREA_LABEL: Record<Area, string> = {
  prep: "Prep — getting an adventure ready",
  table: "The table — running a session",
  players: "Your players",
  data: "Your data — it's yours",
  playing: "Playing — for players",
};
